import { useEffect, useState, useMemo } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Download, Trash2, Sparkles, RefreshCw, Loader2, Link2,
  FileText, Eye, HardDrive, Shield, Printer, FileDown,
} from "lucide-react";
import { api } from "../lib/api";
import { useApp } from "../lib/AppContext";
import Tip from "../components/Tip";
import DatePickerIT from "../components/ui/date-picker-it";
import FinancialItemPickerModal from "../components/FinancialItemPickerModal";
import { formatBytes } from "../lib/format";

const CATEGORIES = {
  fattura: "Fattura", ricevuta: "Ricevuta", contratto: "Contratto",
  referto_amministrativo: "Referto amministrativo", id_documento: "Documento identità",
  scadenza_fiscale: "Scadenza fiscale", estratto_conto: "Estratto conto",
  preventivo: "Preventivo", polizza: "Polizza", certificato: "Certificato",
  nota_spese: "Nota spese", altro: "Altro",
};
const ENTITIES = {
  personal: "Personale", medical: "Medico", prime_vision: "Prime Vision",
  real_estate: "Real estate", writer: "Scrittore", other: "Altro",
};

const eur = (n, c = "EUR") => n == null ? "—" : new Intl.NumberFormat("it-IT", { style: "currency", currency: c }).format(Number(n));

export default function DocumentDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useApp();
  // Role-based visibility (defense-in-depth on top of the backend role guard
  // applied to /api/finances/*). Only the workspace owner and users tagged
  // `admin_finance_hr` see the "Collega a voce finanziaria" affordance.
  const canSeeFinanceLink = Boolean(
    user && (user.is_owner || (Array.isArray(user.roles) && user.roles.includes("admin_finance_hr")))
  );
  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [logs, setLogs] = useState([]);
  const [reprocessing, setReprocessing] = useState(false);
  const [form, setForm] = useState(null);
  const [linkedFin, setLinkedFin] = useState(null);
  const [finPickerOpen, setFinPickerOpen] = useState(false);
  const [linking, setLinking] = useState(false);
  const backendUrl = process.env.REACT_APP_BACKEND_URL || "";

  const load = async () => {
    setLoading(true);
    try {
      const d = await api.docsGet(id);
      setDoc(d);
      setForm({
        title: d.title || "",
        description: d.description || "",
        category: d.category || "altro",
        entity_type: d.entity_type || "personal",
        provider_name: d.provider_name || "",
        document_date: d.document_date || "",
        amount: d.amount ?? "",
        currency: d.currency || "EUR",
        tags: (d.tags || []).join(", "),
        to_send_to_accountant: !!d.to_send_to_accountant,
        fiscal_year: d.fiscal_year || "",
      });
      const l = await api.docsAccessLogs(id, 10);
      setLogs(l.items || []);
      // Fetch linked financial item metadata if any AND user is allowed to
      // see finance data. tech_admin users never make this call so a bug or
      // stale RLS setting cannot leak an amount into the UI.
      if (d.linked_financial_item_id && canSeeFinanceLink) {
        api.financesGetItem(d.linked_financial_item_id)
          .then(setLinkedFin)
          .catch(() => setLinkedFin(null));
      } else {
        setLinkedFin(null);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore caricamento documento");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const linkToFinItem = async (item) => {
    setLinking(true);
    try {
      await api.financesUpdateItem(item.id, { receipt_document_id: id });
      toast.success(`Collegato a "${item.name}"`);
      setFinPickerOpen(false);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore collegamento");
    } finally {
      setLinking(false);
    }
  };

  const unlinkFinItem = async () => {
    if (!linkedFin) return;
    if (!window.confirm(`Scollegare da "${linkedFin.name}"?`)) return;
    setLinking(true);
    try {
      await api.financesUpdateItem(linkedFin.id, { receipt_document_id: null });
      toast.success("Scollegato");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore");
    } finally {
      setLinking(false);
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        title: form.title || null,
        description: form.description || null,
        category: form.category,
        entity_type: form.entity_type,
        provider_name: form.provider_name || null,
        document_date: form.document_date || null,
        amount: form.amount === "" || form.amount == null ? null : parseFloat(form.amount),
        currency: form.currency || "EUR",
        tags: form.tags ? form.tags.split(",").map((t) => t.trim()).filter(Boolean) : null,
        to_send_to_accountant: !!form.to_send_to_accountant,
        fiscal_year: form.fiscal_year === "" || form.fiscal_year == null ? null : parseInt(form.fiscal_year, 10),
      };
      const updated = await api.docsPatch(id, payload);
      setDoc(updated);
      toast.success("Salvato");
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore salvataggio"); }
    finally { setSaving(false); }
  };

  const del = async () => {
    if (!window.confirm("Spostare nel cestino? Potrai ripristinarlo entro 30 giorni.")) return;
    try { await api.docsDelete(id); toast.success("Spostato nel cestino"); nav("/documenti"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
  };

  const reprocess = async () => {
    setReprocessing(true);
    try {
      await api.docsReprocessAI(id);
      toast.success("Analisi IA avviata. Ricarica tra qualche secondo.");
      setTimeout(load, 8000);
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
    finally { setReprocessing(false); }
  };

  const previewUrl = useMemo(() => {
    if (!doc?.preview_url) return null;
    return `${backendUrl}${doc.preview_url}`;
  }, [doc, backendUrl]);

  if (loading || !doc || !form) {
    return <div className="flex items-center gap-2 opacity-70"><Loader2 size={16} className="animate-spin"/> Caricamento…</div>;
  }
  const isImage = (doc.mime_type || "").startsWith("image/");
  const isPdf = doc.mime_type === "application/pdf";
  const canConvertToPdf = isImage;
  const canPrint = isPdf || isImage;
  const conf = doc.ai_confidence;
  const confColor = conf == null ? "#6B7280" : conf >= 0.75 ? "#1F7A2C" : conf >= 0.5 ? "#F5C24A" : "#B42318";

  const doPrint = () => {
    if (!previewUrl) { toast.error("Preview non disponibile per la stampa"); return; }
    // Open the preview URL in a new tab and trigger print once loaded.
    const w = window.open(previewUrl, "_blank", "noopener,noreferrer");
    if (!w) { toast.error("Il browser ha bloccato la finestra di stampa"); return; }
    const tryPrint = () => { try { w.focus(); w.print(); } catch {} };
    // Best-effort: image tab autoloads, PDF may need a delay.
    setTimeout(tryPrint, isPdf ? 1200 : 400);
  };

  return (
    <div className="space-y-4" data-testid="doc-detail-page">
      <div className="flex items-center gap-2 flex-wrap">
        <div className="flex-1"/>
        <Tip label="Riesegui OCR + estrazione IA">
          <button onClick={reprocess} disabled={reprocessing} className="rounded-full border px-3 py-1.5 text-xs inline-flex items-center gap-1 hover:bg-black/5 disabled:opacity-50" data-testid="doc-reprocess">
            {reprocessing ? <Loader2 size={12} className="animate-spin"/> : <RefreshCw size={12}/>} Riesegui IA
          </button>
        </Tip>
        {canPrint && (
          <Tip label="Stampa il documento">
            <button onClick={doPrint} className="rounded-full border px-3 py-1.5 text-xs inline-flex items-center gap-1 hover:bg-black/5" data-testid="doc-print">
              <Printer size={12}/> Stampa
            </button>
          </Tip>
        )}
        {canConvertToPdf && (
          <Tip label="Scarica come PDF (immagine convertita)">
            <a href={`${backendUrl}/api/documents/${id}/download?format=pdf`}
                target="_blank" rel="noreferrer"
                className="rounded-full border px-3 py-1.5 text-xs inline-flex items-center gap-1 hover:bg-black/5" data-testid="doc-download-pdf">
              <FileDown size={12}/> Scarica come PDF
            </a>
          </Tip>
        )}
        <Tip label="Scarica il file originale">
          <a href={`${backendUrl}/api/documents/${id}/download`}
              target="_blank" rel="noreferrer"
              className="rounded-full border px-3 py-1.5 text-xs inline-flex items-center gap-1 hover:bg-black/5" data-testid="doc-download">
            <Download size={12}/> Scarica
          </a>
        </Tip>
        <Tip label="Sposta nel cestino">
          <button onClick={del} className="rounded-full border px-3 py-1.5 text-xs inline-flex items-center gap-1 text-red-700 hover:bg-red-50" data-testid="doc-delete">
            <Trash2 size={12}/> Elimina
          </button>
        </Tip>
      </div>

      <div className="grid lg:grid-cols-2 gap-4">
        {/* LEFT: preview */}
        <div className="pxos-card p-3 min-h-[400px]" data-testid="doc-preview">
          {previewUrl && (isImage || isPdf) ? (
            isImage ? (
              <img src={previewUrl} alt={doc.title} className="w-full max-h-[600px] object-contain rounded" data-testid="doc-preview-img"/>
            ) : (
              <iframe src={previewUrl} title={doc.title} className="w-full h-[600px] rounded border" data-testid="doc-preview-iframe"/>
            )
          ) : (
            <div className="h-full grid place-items-center opacity-60 text-sm">
              <div className="text-center">
                <FileText size={40} className="mx-auto opacity-40"/>
                <div className="mt-2">{doc.mime_type}</div>
                <div className="text-xs mt-1">Preview non supportata · Scarica per aprire</div>
              </div>
            </div>
          )}
          <div className="mt-2 text-xs opacity-60 flex items-center gap-2">
            <HardDrive size={11}/> {formatBytes(doc.size_bytes)} · {doc.mime_type}
            <Shield size={11} className="ms-2"/> Storage privato
          </div>
        </div>

        {/* RIGHT: metadata + AI + audit */}
        <div className="space-y-4">
          {/* AI Extraction card */}
          {doc.ai_extracted_fields && (
            <div className="pxos-card p-3 border-2" style={{ borderColor: "#DDD3F0", background: "#F7F3FF" }} data-testid="doc-ai-card">
              <div className="flex items-center justify-between gap-2 mb-2">
                <div className="flex items-center gap-1.5 text-xs uppercase tracking-widest">
                  <Sparkles size={12} style={{ color: "#5B21B6" }}/> Analisi IA
                </div>
                {conf != null && (
                  <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full" style={{ background: confColor, color: "white" }} data-testid="doc-ai-confidence">
                    {(conf * 100).toFixed(0)}% affidabilità
                  </span>
                )}
              </div>
              <div className="text-xs space-y-1" data-testid="doc-ai-fields">
                {Object.entries(doc.ai_extracted_fields).filter(([, v]) => v != null && v !== "").slice(0, 12).map(([k, v]) => (
                  <div key={k} className="flex justify-between gap-2">
                    <span className="opacity-70">{k}:</span>
                    <span className="text-right font-medium">{String(v).slice(0, 60)}</span>
                  </div>
                ))}
              </div>
              {doc.ai_extracted_at && (
                <div className="text-[10px] opacity-60 mt-2">
                  Ultima analisi: {new Date(doc.ai_extracted_at).toLocaleString("it-IT")}
                </div>
              )}
            </div>
          )}

          {/* Metadata edit */}
          <div className="pxos-card p-3 space-y-2 text-sm" data-testid="doc-metadata">
            <div className="text-xs uppercase tracking-widest opacity-70 mb-1">Metadati</div>
            <label className="block"><span className="text-[10px] uppercase opacity-70 tracking-widest">Titolo</span>
              <input value={form.title} onChange={(e) => setForm({...form, title: e.target.value})} className="pxos-input" data-testid="doc-title"/></label>
            <div className="grid grid-cols-2 gap-2">
              <label className="block"><span className="text-[10px] uppercase opacity-70 tracking-widest">Categoria</span>
                <select value={form.category} onChange={(e) => setForm({...form, category: e.target.value})} className="pxos-input" data-testid="doc-category">
                  {Object.entries(CATEGORIES).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                </select></label>
              <label className="block"><span className="text-[10px] uppercase opacity-70 tracking-widest">Entità</span>
                <select value={form.entity_type} onChange={(e) => setForm({...form, entity_type: e.target.value})} className="pxos-input" data-testid="doc-entity">
                  {Object.entries(ENTITIES).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                </select></label>
            </div>
            <label className="block"><span className="text-[10px] uppercase opacity-70 tracking-widest">Fornitore / Emittente</span>
              <input value={form.provider_name} onChange={(e) => setForm({...form, provider_name: e.target.value})} className="pxos-input" data-testid="doc-provider"/></label>
            <div className="grid grid-cols-2 gap-2">
              <label className="block"><span className="text-[10px] uppercase opacity-70 tracking-widest">Data documento</span>
                <DatePickerIT value={form.document_date} onChange={(v) => setForm({...form, document_date: v})} testId="doc-date"/></label>
              <label className="block"><span className="text-[10px] uppercase opacity-70 tracking-widest">Importo €</span>
                <input type="number" step="0.01" value={form.amount} onChange={(e) => setForm({...form, amount: e.target.value})} className="pxos-input" data-testid="doc-amount"/></label>
            </div>
            <label className="block"><span className="text-[10px] uppercase opacity-70 tracking-widest">Tag (separati da virgola)</span>
              <input value={form.tags} onChange={(e) => setForm({...form, tags: e.target.value})} className="pxos-input" placeholder="es. medico, 2026, rimborsabile" data-testid="doc-tags"/></label>
            <label className="block"><span className="text-[10px] uppercase opacity-70 tracking-widest">Descrizione</span>
              <textarea rows="2" value={form.description} onChange={(e) => setForm({...form, description: e.target.value})} className="pxos-input" data-testid="doc-description"/></label>

            {/* Task 3 · Fatture Commercialista — flag + fiscal year. Hidden
                for roles without finance access (defense-in-depth). */}
            {canSeeFinanceLink && (
              <div className="rounded-xl border p-3 space-y-2 bg-black/5" data-testid="doc-accountant-block">
                <label className="flex items-start gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={!!form.to_send_to_accountant}
                    onChange={(e) => setForm({...form, to_send_to_accountant: e.target.checked})}
                    disabled={!!doc?.sent_to_accountant_at}
                    className="mt-1"
                    data-testid="doc-accountant-flag"
                  />
                  <div className="flex-1">
                    <div className="text-sm font-medium">Da inviare al commercialista</div>
                    <div className="text-xs opacity-70">
                      Il documento comparirà nella pagina "Fatture commercialista" pronto per l'invio.
                    </div>
                    {doc?.sent_to_accountant_at && (
                      <div className="text-xs text-green-700 mt-1" data-testid="doc-accountant-sent-badge">
                        ✓ Inviato il {new Date(doc.sent_to_accountant_at).toLocaleDateString("it-IT")}
                      </div>
                    )}
                  </div>
                </label>
                {form.to_send_to_accountant && (
                  <label className="block">
                    <span className="text-[10px] uppercase opacity-70 tracking-widest">Anno fiscale</span>
                    <input
                      type="number" min="2000" max="2100" step="1"
                      value={form.fiscal_year}
                      onChange={(e) => setForm({...form, fiscal_year: e.target.value})}
                      className="pxos-input"
                      placeholder="es. 2026"
                      data-testid="doc-fiscal-year"
                    />
                  </label>
                )}
              </div>
            )}

            <div className="flex items-center justify-end">
              <button onClick={save} disabled={saving} className="pxos-btn-primary text-xs disabled:opacity-50" data-testid="doc-save">
                {saving ? <Loader2 size={12} className="animate-spin"/> : null} Salva
              </button>
            </div>
          </div>

          {/* Links (Enhancement A: bidirectional) — hidden for roles without
              finance access. tech_admin doesn't even render the section. */}
          {canSeeFinanceLink && (
          <div className="pxos-card p-3 text-sm" data-testid="doc-links">
            <div className="text-xs uppercase tracking-widest opacity-70 flex items-center gap-1 mb-2"><Link2 size={11}/> Collegamenti</div>
            {linkedFin ? (
              <div className="flex items-center gap-2 flex-wrap" data-testid="doc-linked-fin-item">
                <Link
                  to={`/finances/items?highlight=${linkedFin.id}`}
                  className="flex-1 min-w-0 pxos-card p-2.5 hover:bg-[var(--pxos-surface-soft)] flex items-center gap-2 text-xs no-underline"
                  style={{ background: "#F0F7F1", borderColor: "#B7DCC0" }}
                  data-testid="doc-linked-fin-item-link"
                >
                  <span className="text-[10px] uppercase tracking-widest px-1.5 py-0.5 rounded-full font-semibold flex-shrink-0"
                        style={{ background: "#1F7A2C", color: "white" }}>Finanza</span>
                  <span className="flex-1 min-w-0">
                    <span className="font-semibold truncate block">{linkedFin.name}</span>
                    <span className="opacity-70">{eur(linkedFin.amount_expected, linkedFin.currency || "EUR")}
                    {linkedFin.due_date && <> · scad. {new Date(linkedFin.due_date).toLocaleDateString("it-IT")}</>}</span>
                  </span>
                </Link>
                <button
                  onClick={unlinkFinItem}
                  disabled={linking}
                  className="rounded-full border px-2.5 py-1 text-[10px] font-semibold text-red-700 hover:bg-red-50 disabled:opacity-50"
                  data-testid="doc-unlink-fin-item"
                >
                  Scollega
                </button>
              </div>
            ) : (
              <div className="space-y-2">
                <button
                  onClick={() => setFinPickerOpen(true)}
                  className="w-full inline-flex items-center justify-center gap-2 rounded-full border border-dashed px-3 py-2 text-xs font-semibold hover:bg-[var(--pxos-surface-soft)]"
                  style={{ borderColor: "var(--pxos-border)" }}
                  data-testid="doc-link-fin-item-btn"
                >
                  <Link2 size={12}/> Collega a voce finanziaria esistente
                </button>
                {doc.amount != null && doc.category === "fattura" && (
                  <Link
                    to={`/finances/items?new=1&name=${encodeURIComponent(doc.provider_name || doc.title || "")}&amount=${doc.amount}&receipt=${id}`}
                    className="w-full inline-flex items-center justify-center gap-2 rounded-full border px-3 py-2 text-xs font-semibold text-blue-700 hover:bg-blue-50"
                    style={{ borderColor: "#C3DAF4" }}
                    data-testid="doc-create-fin-item"
                  >
                    + Crea nuova voce collegata (importo pre-compilato)
                  </Link>
                )}
              </div>
            )}
          </div>
          )}

          {/* Access logs */}
          <div className="pxos-card p-3 text-sm" data-testid="doc-access-logs">
            <div className="text-xs uppercase tracking-widest opacity-70 flex items-center gap-1 mb-1"><Eye size={11}/> Ultimi accessi</div>
            {logs.length === 0 ? (
              <div className="text-xs opacity-60">Nessun accesso registrato.</div>
            ) : (
              <ul className="text-xs space-y-0.5" data-testid="doc-logs-list">
                {logs.map((l, i) => (
                  <li key={i} className="flex justify-between gap-2">
                    <span>{ACTION_LABELS[l.action] || l.action}</span>
                    <span className="opacity-60">{new Date(l.accessed_at).toLocaleString("it-IT")}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>
      <FinancialItemPickerModal
        open={finPickerOpen}
        onClose={() => setFinPickerOpen(false)}
        currentId={id}
        onSelect={linkToFinItem}
      />
    </div>
  );
}

const ACTION_LABELS = {
  uploaded: "Caricato", viewed: "Visualizzato", downloaded: "Scaricato",
  edited: "Modificato", deleted: "Eliminato", shared: "Condiviso",
  restored: "Ripristinato", reprocessed: "Ri-analizzato IA",
};
